import {
  createContext,
  PropsWithChildren,
  useContext,
  useEffect,
  useEffectEvent,
  useSyncExternalStore,
} from 'react';
import { io, Socket } from 'socket.io-client';

import { OrderModel } from '@portal-clients/order';
import { TransactionV2EventModel } from '@portal-clients/transaction-v2';

import { getAuthToken } from '@frontend/auth';

export interface UpdateSocketEvents {
  transaction: (transaction: TransactionV2EventModel) => void;
  order: (order: OrderModel) => void;
}

export interface UpdateContextType {
  socket: Socket<UpdateSocketEvents>;
}

export interface SocketConnectionStoreState {
  isConnected: boolean;

  /**
   * Can be used to determine if a reconnection has occurred.
   * Every time a new connection is made, this value will be
   * incremented.
   */
  connectionCounter: number;
}

class SocketConnectionStore {
  private store: SocketConnectionStoreState;
  private eventTarget: EventTarget;

  constructor() {
    this.store = { isConnected: false, connectionCounter: 0 };
    this.eventTarget = new EventTarget();
  }

  getSnapshot = (): SocketConnectionStoreState => {
    return this.store;
  };

  set(isConnected: boolean): void {
    const isChanged = this.store.isConnected !== isConnected;

    this.store = {
      isConnected,
      connectionCounter: isConnected
        ? this.store.connectionCounter + 1
        : this.store.connectionCounter,
    };

    if (isChanged) {
      this.eventTarget.dispatchEvent(new Event('changed'));
    }
  }

  subscribe = (callback: () => void): (() => void) => {
    this.eventTarget.addEventListener('changed', callback);

    return () => {
      this.eventTarget.removeEventListener('changed', callback);
    };
  };
}

const socketConnectionStore = new SocketConnectionStore();
const UpdateContext = createContext<UpdateContextType>(null!);

const socket = io(WS_CONFIG?.host ? WS_CONFIG.host : undefined, {
  autoConnect: false,
  path: '/update-events',
  protocols: ['websocket'],
  auth: (cb) => {
    getAuthToken().then((token) => {
      cb({
        token,
      });
    });
  },
});

socket.on('connect', () => {
  console.log('Connected to update socket');
  socketConnectionStore.set(true);
});

socket.on('connect_error', (error: Error) => {
  socketConnectionStore.set(false);

  if (socket.active) {
    // temporary failure, the socket will automatically try to reconnect
  } else {
    // the connection was denied by the server
    // in that case, `socket.connect()` must be manually called in order to
    // reconnect. This happens if the user session is no longer valid.
    console.error('Failed to connect to update socket:', error);
  }
});

socket.on('disconnect', () => {
  console.log('Disconnected from update socket');
  socketConnectionStore.set(false);
});

export const UpdateProvider = (props: PropsWithChildren) => {
  useEffect(() => {
    socket.connect();

    return () => {
      socket.disconnect();
    };
  }, []);

  return (
    <UpdateContext.Provider value={{ socket }}>
      {props.children}
    </UpdateContext.Provider>
  );
};

export const useUpdateSocket = () => {
  return useContext(UpdateContext);
};

export function useSocketEvent<TEventName extends keyof UpdateSocketEvents>(
  name: TEventName,
  cb: UpdateSocketEvents[TEventName],
) {
  const context = useUpdateSocket();

  useEffect(() => {
    context?.socket.on(name, cb as any);

    return () => {
      context?.socket.off(name, cb as any);
    };
  }, [name, cb]);
}

/**
 * Can be used to trigger actions when a socket reconnect occurs.
 */
export const useSocketReconnectEvent = (cb: () => void) => {
  const { isConnected, connectionCounter } = useSyncExternalStore(
    socketConnectionStore.subscribe,
    socketConnectionStore.getSnapshot,
  );

  const handleReconnect = useEffectEvent(cb);

  useEffect(() => {
    if (isConnected && connectionCounter > 1) {
      handleReconnect();
    }
  }, [isConnected, connectionCounter]);
};
